// 📁 frontend/src/utils.js
import { openDB } from "idb";
import { api, ApiError } from "./api/client";

const REACT_APP_EASYJOB_ENV = process.env.REACT_APP_EASYJOB_ENV || "local";

export function getOrCreateDeviceId() {
  const key = "ej_device_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

const DB_NAME = "easyjob-db";
const DB_VERSION = 2605041200;
const STORES = {
  RICHIESTE: "richieste",
  TIPOLGIE: "RichiesteTipologia",
  PREFERENZE: "PreferenzeUtente",
  TIMBRATE: "timbrate",
  TIMBRATE_SCARTATE: "timbrate_scartate",
  PIANIFICAZIONI: "pianificazioni",
  LOCATION_CONTEXT: "location_context",
  ERROR_WSS: "error_wss",
};

export const TIMBRATURE_STORAGE_PREFIX = "timbrature_giornata";

/**
 * Schema IndexedDB (easyjob-db v5)
 * - richieste: offline richieste da inviare al backend (keyPath=id autoincrement)
 * - RichiesteTipologia: cache tipologie richieste (keyPath=id)
 * - PreferenzeUtente: chiave libera per preferenze/login cache
 * - timbrate: coda di timbrature catturate offline ({ id, user_id, tipo, orario, data_riferimento, timeframe_id, device_id, qr, data_sync })
 */

/**
 * Converte una data JS o stringa in formato ISO string (YYYY-MM-DD o YYYY-MM-DD HH:mm)
 * @param {Date|string} date
 * @param {boolean} mostraOrario - se true aggiunge l'orario (HH:mm)
 * @returns {string} ISO date string
 */
// Converte Date/stringa in formato ISO (YYYY-MM-DD o con orario)
export function formatIsoDate(date, mostraOrario = false) {
  if (!date) return "-";
  let d;
  if (typeof date === "string") {
    d = new Date(date);
    if (isNaN(d.getTime())) return date; // fallback
  } else if (date instanceof Date) {
    d = date;
  } else {
    return "-";
  }

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  if (mostraOrario) {
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  } else {
    return `${yyyy}-${mm}-${dd}`;
  }
}

// Restituisce una data formattata in italiano, con opzioni orario/anno breve
export function formatItalianDate(dateString, mostraOrario = false, annoDueCifre = false) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  let date = d.toLocaleDateString("it-IT");
  if (annoDueCifre) {
    const [giorno, mese, anno] = date.split("/");
    date = `${giorno}/${mese}/${anno.slice(-2)}`;
  }
  if (mostraOrario) {
    return `${date} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  } else {
    return date;
  }
}

// Rileva lo stato di connettività del browser
export function isOnline() {
  return navigator.onLine;
}

// Somma secondi (positivi o negativi) a una data e restituisce un nuovo oggetto Date
export function addSecToDate(date, seconds = 0) {
  if (!date && date !== 0) return null;
  const base = date instanceof Date ? new Date(date) : new Date(date);
  if (Number.isNaN(base.getTime())) return null;
  const next = new Date(base.getTime() + seconds * 1000);
  return next;
}

// Genera la chiave localStorage per le timbrate di una data
export function storageKeyForDate(date) {
  return `${TIMBRATURE_STORAGE_PREFIX}_${date}`;
}

// Tenta di decodificare l'array di timbrate salvato in localStorage
export function parseStoredTimbrature(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Impossibile leggere le timbrature locali", err);
    return [];
  }
}

// Apre (o crea) l'istanza condivisa di IndexedDB con tutti gli store EasyJob
export async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      if (!db.objectStoreNames.contains(STORES.RICHIESTE)) {
        db.createObjectStore(STORES.RICHIESTE, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.TIPOLGIE)) {
        db.createObjectStore(STORES.TIPOLGIE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.PREFERENZE)) {
        db.createObjectStore(STORES.PREFERENZE);
      }
      if (!db.objectStoreNames.contains(STORES.TIMBRATE)) {
        const store = db.createObjectStore(STORES.TIMBRATE, { keyPath: "id" });
        store.createIndex("by_data_riferimento", "data_riferimento");
        store.createIndex("by_data_sync", "data_sync");
      } else if (transaction) {
        const store = transaction.objectStore(STORES.TIMBRATE);
        if (store && !store.indexNames.contains("by_data_riferimento")) {
          store.createIndex("by_data_riferimento", "data_riferimento");
        }
        if (store && !store.indexNames.contains("by_data_sync")) {
          store.createIndex("by_data_sync", "data_sync");
        }
      }
      if (!db.objectStoreNames.contains(STORES.TIMBRATE_SCARTATE)) {
        const store = db.createObjectStore(STORES.TIMBRATE_SCARTATE, { keyPath: "id" });
        store.createIndex("by_user", "user_id");
        store.createIndex("by_result", "gps_result");
        store.createIndex("by_created_at", "created_at");
      } else if (transaction) {
        const store = transaction.objectStore(STORES.TIMBRATE_SCARTATE);
        if (store && !store.indexNames.contains("by_user")) {
          store.createIndex("by_user", "user_id");
        }
        if (store && !store.indexNames.contains("by_result")) {
          store.createIndex("by_result", "gps_result");
        }
        if (store && !store.indexNames.contains("by_created_at")) {
          store.createIndex("by_created_at", "created_at");
        }
      }
      if (!db.objectStoreNames.contains(STORES.PIANIFICAZIONI)) {
        const store = db.createObjectStore(STORES.PIANIFICAZIONI, { keyPath: "id" });
        store.createIndex("by_user", "user_id");
        store.createIndex("by_user_date", ["user_id", "data_riferimento"]);
      } else if (transaction) {
        const store = transaction.objectStore(STORES.PIANIFICAZIONI);
        if (store && !store.indexNames.contains("by_user")) {
          store.createIndex("by_user", "user_id");
        }
        if (store && !store.indexNames.contains("by_user_date")) {
          store.createIndex("by_user_date", ["user_id", "data_riferimento"]);
        }
      }
      if (!db.objectStoreNames.contains(STORES.LOCATION_CONTEXT)) {
        const store = db.createObjectStore(STORES.LOCATION_CONTEXT, { keyPath: "id" });
        store.createIndex("by_user", "user_id");
        store.createIndex("by_assunzione", "assunzione_id");
      } else if (transaction) {
        const store = transaction.objectStore(STORES.LOCATION_CONTEXT);
        if (store && !store.indexNames.contains("by_user")) {
          store.createIndex("by_user", "user_id");
        }
        if (store && !store.indexNames.contains("by_assunzione")) {
          store.createIndex("by_assunzione", "assunzione_id");
        }
      }
      if (!db.objectStoreNames.contains(STORES.ERROR_WSS)) {
        const store = db.createObjectStore(STORES.ERROR_WSS, { keyPath: "id", autoIncrement: true });
        store.createIndex("by_ts", "ts");
      }
    },
    blocked() {
      // Una connessione più vecchia (altro tab/PWA) è ancora aperta e impedisce
      // l'upgrade. Il reload automatico legato al service worker (vedi App.js)
      // di norma la chiude; questo log resta come traccia diagnostica.
      console.warn("[IndexedDB] Apertura bloccata: una connessione più vecchia è ancora aperta altrove.");
    },
    blocking(currentVersion, blockedVersion, event) {
      // Questa connessione sta impedendo l'apertura di una versione più nuova
      // altrove (es. un altro tab appena aggiornato) — la chiudiamo per
      // lasciarla procedere invece di bloccarla indefinitamente.
      event.target.close();
    },
  });
}

const ERROR_WSS_MAX = 100;

export async function logWssError({ endpoint, payload, response, status }) {
  try {
    const db = await getDB();
    await db.add(STORES.ERROR_WSS, { endpoint, payload, response, status, ts: new Date().toISOString() });
    const all = await db.getAllFromIndex(STORES.ERROR_WSS, "by_ts");
    if (all.length > ERROR_WSS_MAX) {
      const toDelete = all.slice(0, all.length - ERROR_WSS_MAX);
      for (const rec of toDelete) await db.delete(STORES.ERROR_WSS, rec.id);
    }
  } catch (_) {}
}

// Accoda una richiesta giustificativi offline nell'IndexedDB
export async function saveRequestOffline(request) {
  const db = await getDB();
  await db.add(STORES.RICHIESTE, request);
}

// Prova a inviare tutte le richieste offline e pulisce quelle riuscite
// Richiede l'utente corrente per aggiungere il session_token fresco al momento della sync
export async function syncRichieste(user) {
  const db = await getDB();
  const allRequests = await db.getAll(STORES.RICHIESTE);
  let synced = 0;
  for (const req of allRequests) {
    try {
      await api.post("/api/richieste", { ...req, session_token: user?.token });
      await db.delete(STORES.RICHIESTE, req.id);
      synced += 1;
    } catch (err) {
      // Lascia la richiesta in locale se fallisce
    }
  }
  return { ok: true, synced };
}

// Salva in cache le tipologie disponibili per lavorare offline
export async function saveTipologieOffline(tipologie) {
  const db = await getDB();
  const tx = db.transaction(STORES.TIPOLGIE, "readwrite");
  const store = tx.objectStore(STORES.TIPOLGIE);
  await store.clear();
  for (const t of tipologie) {
    // Assicurati che ogni tipologia abbia il campo ultimo_utilizzato (default false)
    await store.put({ ...t, ultimo_utilizzato: t.ultimo_utilizzato ?? false });
  }
  await tx.done;
}

// Recupera le tipologie dalla cache locale
export async function getTipologieOffline() {
  const db = await getDB();
  return db.getAll(STORES.TIPOLGIE);
}

// Salva nelle preferenze l'ultima tipologia scelta per una certa interfaccia
export async function setUltimaTipologiaUtilizzata(id, descrizione, interfaccia = "NuovaRichiesta") {
  const db = await getDB();
  const tx = db.transaction(STORES.PREFERENZE, "readwrite");
  const store = tx.objectStore(STORES.PREFERENZE);
  await store.put({ interfaccia, id, descrizione }, interfaccia); 
  await tx.done;
}

// Legge la tipologia preferita per l'interfaccia indicata
export async function getUltimaTipologiaUtilizzata(interfaccia = "NuovaRichiesta") {
  const db = await getDB();
  return db.get(STORES.PREFERENZE, interfaccia);
}

// Memorizza l'ultimo utente autenticato per permettere il login offline rapido
export async function saveUserOffline(user) {
  const db = await getDB();
  await db.put(STORES.PREFERENZE, user, "lastUser");
}

// Restituisce l'ultimo utente salvato localmente
export async function getUserOffline() {
  const db = await getDB();
  return db.get(STORES.PREFERENZE, "lastUser");
}

// Chiama il backend per aggiornare i residui ferie/permessi dell'utente
export async function aggiornaResidui(user) {
  if (!user?.assunzione_id || !user?.token) return user;

  let data;
  try {
    data = await api.post("/api/residui", {
      assunzione_id: user.assunzione_id,
      session_token: user.token,
    });
  } catch (err) {
    console.error("Residui non aggiornati", err);
    return user;
  }

  // Calculate the sums for ferie and permessi
  const residui = data.assunzione?.residui || {};
  residui.ferie_totali = Number(residui.ferie || 0) + Number(residui.previsto_ferie || 0);
  residui.permessi_totali = Number(residui.permessi || 0) + Number(residui.previsto_permessi || 0);

  // Update the user object with the new response structure
  return {
    ...user,
    residui,
    matricola: data.assunzione?.matricola || user.matricola || null,
  };
}

// Normalizza un payload timbratura assicurando id e data_riferimento
function ensureTimbrataRecord(record) {
  if (!record) return null;
  const id = record.id || `timbrata_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const data_riferimento = record.data_riferimento || (record.orario ? record.orario.slice(0, 10) : null);
  return { ...record, id, data_riferimento, data_sync: record.data_sync ?? null };
}

// Accoda una timbrata nella store locale in attesa di sincronizzazione
export async function saveTimbrataOffline(record) {
  const sanitized = ensureTimbrataRecord(record);
  if (!sanitized?.user_id && !sanitized?.assunzione_id) {
    console.warn("Timbrata senza identificativo utente: impossibile salvarla nella coda offline");
    return;
  }
  const db = await getDB();
  await db.put(STORES.TIMBRATE, sanitized);
}

// Conta le timbrate offline non ancora sincronizzate (opzionale per utente)
export async function countPendingTimbrate(userId) {
  const db = await getDB();
  const all = await db.getAll(STORES.TIMBRATE);
  const pending = all.filter(t => !t.data_sync);
  return userId ? pending.filter(t => t.user_id === userId).length : pending.length;
}

// Resetta retry_count su tutte le timbrate pending di un utente (override manuale)
export async function resetRetryCount(userId) {
  const db = await getDB();
  const all = (await db.getAll(STORES.TIMBRATE)).filter(r => r.user_id === userId && !r.data_sync && (r.retry_count ?? 0) > 0);
  for (const rec of all) {
    await db.put(STORES.TIMBRATE, { ...rec, retry_count: 0, ultimo_errore: null });
  }
}

// Resetta retry_count su una singola timbrata (retry manuale per record)
export async function resetSingleRetry(id) {
  const db = await getDB();
  const rec = await db.get(STORES.TIMBRATE, id);
  if (rec) await db.put(STORES.TIMBRATE, { ...rec, retry_count: 0, ultimo_errore: null });
}

// Elimina tutte le timbrate di un giorno (e utente) già sincronizzate
export async function deleteTimbrateByDate(date, userId) {
  const db = await getDB();
  const tx = db.transaction(STORES.TIMBRATE, "readwrite");
  const store = tx.objectStore(STORES.TIMBRATE);
  let cursor = await store.openCursor();
  while (cursor) {
    const value = cursor.value;
    if (value && value.data_riferimento === date && (!userId || value.user_id === userId)) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
  await tx.done;
}

// Converte un'ISO date in formato yyyy-MM-dd HH:mm richiesto dal WSS Cedolino
function formatTimbrataWssDate(isoDate) {
  const date = isoDate ? new Date(isoDate) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

// Converte un'ISO date in formato ddMMyyHHmm richiesto dal WS Cedolino
function formatTimbrataWsDate(isoDate) {
  const date = isoDate ? new Date(isoDate) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}${mm}${yy}${hh}${min}`;
}

// Prepara il payload compatibile con il WS create_timbrata_originale
function buildTimbrataPayload(record, cedolinoUserId) {
  const dateParam = formatTimbrataWssDate(record.orario);
  if (!cedolinoUserId) throw new Error("Utente non valido: ripeti il login.");
  if (!dateParam) throw new Error("Data timbratura non valida.");
  if (!record?.timeframe_id) throw new Error("Timeframe mancante.");
  if (!record?.data_riferimento) throw new Error("Data di riferimento mancante.");
  return {
    user: String(record.user_id || cedolinoUserId),
    assunzione_id: String(cedolinoUserId),
    date: dateParam,
    uuid: record.device_id || "",
    timeframe_id: String(record.timeframe_id),
    data_riferimento: record.data_riferimento,
    lat: record.lat ?? null,
    lng: record.lng ?? null,
  };
}

const MAX_SYNC_RETRY = 3;

// Sincronizza in coda tutte le timbrate locali dell'utente
export async function syncTimbrate(user) {
  if (!user?.id) {
    return { ok: false, error: "Utente non valido.", synced: 0 };
  }
  const db = await getDB();
  const pending = (await db.getAll(STORES.TIMBRATE))
    .filter(rec => rec.user_id === user.id && !rec.data_sync && (rec.retry_count ?? 0) < MAX_SYNC_RETRY)
    .sort((a, b) => new Date(a.orario).getTime() - new Date(b.orario).getTime());

  if (!pending.length) {
    return { ok: true, synced: 0, clearedDates: [] };
  }

  const clearedDates = new Set();
  const errors = [];
  let synced = 0;

  for (const record of pending) {
    let builtPayload = null;
    let httpStatus = null;
    let responseBody = null;
    try {
      const cedolinoUserId = record.assunzione_id || user.assunzione_id || user.id;
      builtPayload = { ...buildTimbrataPayload(record, cedolinoUserId) };
      responseBody = await api.post("/api/timbrature", {
        ...builtPayload,
        session_token: user?.token,
        azienda_id: user?.cliente_id,
        sync_timbrate_cedo: user?.sync_timbrate_cedo,
      });
      httpStatus = 200;
      if (responseBody?.success === false) {
        throw new Error(responseBody?.error || "Errore sincronizzazione timbrature.");
      }
      const syncedAt = new Date().toISOString();
      const syncedRecord = { ...record, data_sync: syncedAt, ultimo_errore: null };
      await db.put(STORES.TIMBRATE, syncedRecord);
      if (record.data_riferimento) {
        const sameDay = (await db.getAllFromIndex(STORES.TIMBRATE, "by_data_riferimento", record.data_riferimento)).filter(r => r.user_id === record.user_id);
        const hasFine = sameDay.some(r => r.tipo === "FINE_GIORNATA");
        const allSynced = sameDay.length > 0 && sameDay.every(r => r.data_sync);
        if (hasFine && allSynced) {
          await deleteTimbrateByDate(record.data_riferimento, record.user_id);
          clearedDates.add(record.data_riferimento);
        }
      }
      synced += 1;
    } catch (err) {
      if (err instanceof ApiError) {
        httpStatus = err.status;
        responseBody = err.body;
      }
      errors.push({ id: record.id, error: err.message });
      try {
        await db.put(STORES.TIMBRATE, { ...record, ultimo_errore: err.message, retry_count: (record.retry_count ?? 0) + 1 });
      } catch (_) {}
      await logWssError({ endpoint: "createTimbrataOriginaleUser", payload: builtPayload, response: responseBody ?? err.message, status: httpStatus });
    }
  }

  return { ok: errors.length === 0, synced, errors, clearedDates: Array.from(clearedDates) };
}

// Lancia la sincronizzazione completa (richieste + timbrate dell'utente)
export async function syncAll({ user } = {}) {
  const result = {};
  try {
    result.richieste = await syncRichieste(user);
  } catch (err) {
    result.richieste = { ok: false, error: err.message };
  }

  if (user) {
    try {
      result.timbrate = await syncTimbrate(user);
    } catch (err) {
      result.timbrate = { ok: false, error: err.message };
    }
  }

  return result;
}
